import { useState, useCallback, useEffect, useRef } from 'react';
import {
  fetchPerson,
  searchPersons,
  searchTeams,
  searchMatchHubPlayers,
  fetchTeamByExternalId,
} from '../api';

/**
 * Custom hook for managing the PersonModal — a three-phase player picker:
 *
 *  - `search`: single-input autocomplete; click a result to assign, or
 *    click "Create new player" to expand into the create form.
 *  - `create`: form pre-filled from the search query split on first
 *    whitespace (first word → first_name, rest → last_name). Optional
 *    "+ Add national team" toggle reveals a second team picker so a
 *    Dutch international tagged at a Feyenoord match can get both
 *    memberships in one save.
 *  - `edit`: same form as create, pre-filled from `fetchPerson(...)`.
 *    Triggered when openPersonModal is called with a personId (a tagged
 *    face).
 *
 * Mirrors matrix-app's `PhotoEditView` flow so editors get the same UX
 * on both surfaces.
 *
 * @param {{ matchId?: string | null }} options
 * @returns {Object} hook state + handlers consumed by PersonModal
 */
export default function usePersonForm({ matchId = null } = {}) {
  // Modal visibility and position
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [personModalPos, setPersonModalPos] = useState({ x: 0, y: 0 });
  const personModalDrag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
  });

  // Phase routing
  const [phase, setPhase] = useState('search'); // 'search' | 'create' | 'edit'

  // Search query (search phase only)
  const [query, setQuery] = useState('');

  // Form state (create + edit phases)
  const [personForm, setPersonForm] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    suffix: '',
    jersey_number: '',
    team_id: '',
  });
  const [modalPersonId, setModalPersonId] = useState(null);
  const [personSuggestions, setPersonSuggestions] = useState([]);
  // Round 2 (Goal 3.4): parallel MatchHub player suggestions in the same search
  // phase. Rendered as a second group ("From MatchHub") below the matrix list.
  const [matchHubSuggestions, setMatchHubSuggestions] = useState([]);
  // When the editor picks a matchhub-only result, we stash the source row so
  // the create form can prefill name + conditionally attach the team (only if
  // matchhub's current_team has a local Team by external_id) + carry the
  // external_id through to the POST.
  const [matchHubPrefill, setMatchHubPrefill] = useState(null);
  const [suggestTimer, setSuggestTimer] = useState(null);

  // Primary team search state (used in create + edit)
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSearchTimer, setTeamSearchTimer] = useState(null);

  // Secondary team picker (national-team toggle, create phase only)
  const [showNationalTeamPicker, setShowNationalTeamPicker] = useState(false);
  const [nationalTeamSearchQuery, setNationalTeamSearchQuery] = useState('');
  const [nationalTeamSuggestions, setNationalTeamSuggestions] = useState([]);
  const [selectedNationalTeam, setSelectedNationalTeam] = useState(null);
  const [nationalTeamSearchTimer, setNationalTeamSearchTimer] = useState(null);

  const resetForm = useCallback(() => {
    setPersonForm({
      prefix: '',
      first_name: '',
      last_name: '',
      suffix: '',
      jersey_number: '',
      team_id: '',
    });
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setSelectedTeam(null);
    setShowNationalTeamPicker(false);
    setNationalTeamSearchQuery('');
    setNationalTeamSuggestions([]);
    setSelectedNationalTeam(null);
  }, []);

  // Open person modal: edit mode if personId/face.person, search mode otherwise
  const openPersonModal = useCallback(
    async ({ face = null, personId = null } = {}) => {
      const resolvedPersonId = personId || (face ? face.person : null);
      setModalPersonId(resolvedPersonId);
      setQuery('');
      setPersonSuggestions([]);
      resetForm();

      if (resolvedPersonId) {
        setPhase('edit');
        try {
          const p = await fetchPerson(resolvedPersonId);
          const firstMembership = p.team_memberships?.[0];
          setPersonForm({
            prefix: p.prefix || '',
            first_name: p.first_name || '',
            last_name: p.last_name || '',
            suffix: p.suffix || '',
            jersey_number: p.jersey_number ?? '',
            team_id: firstMembership?.team_id || '',
          });
          if (firstMembership?.team_id) {
            setSelectedTeam({
              id: firstMembership.team_id,
              name: firstMembership.team_name,
            });
            setTeamSearchQuery(firstMembership.team_name || '');
          }
        } catch (e) {
          console.error(e);
        }
      } else {
        setPhase('search');
      }

      setShowPersonModal(true);
      setPersonModalPos({ x: 0, y: 0 });
    },
    [resetForm],
  );

  // Close person modal
  const closePersonModal = useCallback(() => {
    setShowPersonModal(false);
  }, []);

  // Search → Create transition: split query on first whitespace.
  // Mirrors matrix-app PhotoEditView (split first_name = words[0],
  // last_name = words.slice(1).join(' ')) so the editor doesn't retype
  // what they already searched for.
  const enterCreatePhase = useCallback(() => {
    const words = query.trim().split(/\s+/).filter(Boolean);
    setPersonForm((prev) => ({
      ...prev,
      first_name: words[0] || '',
      last_name: words.slice(1).join(' '),
    }));
    setMatchHubPrefill(null);
    setPhase('create');
  }, [query]);

  // Search → Create starting from a MatchHub-only result (Goal 3.4). Prefills
  // the name from MatchHub. Conditional team attach: looks up the matchhub
  // player's current_team external_id in local Teams; if present, pre-selects
  // it as the team membership. If not, leaves team blank — never auto-creates
  // a local Team from the face-tag flow (per overarching principle 2).
  const enterCreateFromMatchHub = useCallback(async (matchhubPlayer) => {
    const splitParts = (matchhubPlayer?.name || '').trim().split(/\s+/).filter(Boolean);
    const first = matchhubPlayer?.firstname || splitParts[0] || '';
    const last =
      matchhubPlayer?.lastname || splitParts.slice(1).join(' ') || '';
    setPersonForm((prev) => ({
      ...prev,
      first_name: first,
      last_name: last,
      jersey_number: '',
      team_id: '',
    }));
    setMatchHubPrefill({
      external_id: matchhubPlayer?.id,
      confirmed_name: matchhubPlayer?.name || `${first} ${last}`.trim(),
      current_team_external_id:
        matchhubPlayer?.current_team_external_id ||
        matchhubPlayer?.team?.id ||
        null,
    });
    setSelectedTeam(null);
    setTeamSearchQuery('');
    // Try to resolve MatchHub's current_team to a local Team. Auto-attach when
    // we find one (Yannick's clarification: team link is conditional, never
    // creating new teams in this flow).
    const teamExtId =
      matchhubPlayer?.current_team_external_id || matchhubPlayer?.team?.id || null;
    if (teamExtId) {
      try {
        const localTeam = await fetchTeamByExternalId(teamExtId);
        if (localTeam) {
          setSelectedTeam(localTeam);
          setTeamSearchQuery(localTeam.name);
          setPersonForm((prev) => ({ ...prev, team_id: localTeam.id }));
        }
      } catch (_e) {
        // best-effort; no team attached
      }
    }
    setPhase('create');
  }, []);

  // Back button from create → search; preserves query.
  const enterSearchPhase = useCallback(() => {
    setPhase('search');
  }, []);

  const toggleNationalTeamPicker = useCallback(() => {
    setShowNationalTeamPicker((prev) => {
      if (prev) {
        setNationalTeamSearchQuery('');
        setNationalTeamSuggestions([]);
        setSelectedNationalTeam(null);
      }
      return !prev;
    });
  }, []);

  // Handle modal drag start
  const handleModalDragStart = useCallback(
    (e) => {
      if (e.target.closest('button')) return;
      e.preventDefault();
      personModalDrag.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        startPosX: personModalPos.x,
        startPosY: personModalPos.y,
      };
    },
    [personModalPos],
  );

  // Debounced person search (search phase only). Round 2: queries Matrix AND
  // MatchHub in parallel via Promise.all so the editor sees both result groups
  // resolve as a single render (no partial flicker / no ordering race).
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Debounced search pattern
  useEffect(() => {
    if (suggestTimer) clearTimeout(suggestTimer);

    if (phase !== 'search' || !query || query.trim().length < 2) {
      setPersonSuggestions([]);
      setMatchHubSuggestions([]);
      setSuggestTimer(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const trimmed = query.trim();
        const [matrixResults, matchhubData] = await Promise.all([
          searchPersons(trimmed, matchId).catch(() => []),
          searchMatchHubPlayers(trimmed).catch(() => ({ players: [] })),
        ]);
        setPersonSuggestions(matrixResults || []);
        setMatchHubSuggestions(matchhubData?.players || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    setSuggestTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, phase, matchId]);

  // Debounced primary team search.
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Debounced search pattern
  useEffect(() => {
    if (teamSearchTimer) clearTimeout(teamSearchTimer);

    if (selectedTeam && teamSearchQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      setTeamSearchTimer(null);
      return;
    }

    if (!teamSearchQuery || teamSearchQuery.length < 2) {
      setTeamSuggestions([]);
      setTeamSearchTimer(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await searchTeams(teamSearchQuery);
        setTeamSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    setTeamSearchTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSearchQuery, selectedTeam]);

  // Debounced national-team search (only when picker visible).
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Debounced search pattern
  useEffect(() => {
    if (nationalTeamSearchTimer) clearTimeout(nationalTeamSearchTimer);

    if (!showNationalTeamPicker) {
      setNationalTeamSuggestions([]);
      setNationalTeamSearchTimer(null);
      return;
    }

    if (
      selectedNationalTeam &&
      nationalTeamSearchQuery === selectedNationalTeam.name
    ) {
      setNationalTeamSuggestions([]);
      setNationalTeamSearchTimer(null);
      return;
    }

    if (!nationalTeamSearchQuery || nationalTeamSearchQuery.length < 2) {
      setNationalTeamSuggestions([]);
      setNationalTeamSearchTimer(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await searchTeams(nationalTeamSearchQuery);
        // Bias the dropdown towards national teams so the toggle's
        // purpose is obvious — but don't hard-filter; the editor may
        // legitimately want a second club affiliation later.
        const sorted = (res || []).slice().sort((a, b) => {
          const aNat = a.team_type === 'national' ? 0 : 1;
          const bNat = b.team_type === 'national' ? 0 : 1;
          return aNat - bNat;
        });
        setNationalTeamSuggestions(sorted);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    setNationalTeamSearchTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationalTeamSearchQuery, selectedNationalTeam, showNationalTeamPicker]);

  // Select primary team
  const selectTeam = useCallback((team) => {
    setSelectedTeam(team);
    setTeamSearchQuery(team.name);
    setTeamSuggestions([]);
    setPersonForm((prev) => ({ ...prev, team_id: team.id }));
  }, []);

  // Clear primary team
  const clearTeam = useCallback(() => {
    setSelectedTeam(null);
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setPersonForm((prev) => ({ ...prev, team_id: '' }));
  }, []);

  // Select national team
  const selectNationalTeam = useCallback((team) => {
    setSelectedNationalTeam(team);
    setNationalTeamSearchQuery(team.name);
    setNationalTeamSuggestions([]);
  }, []);

  const clearNationalTeam = useCallback(() => {
    setSelectedNationalTeam(null);
    setNationalTeamSearchQuery('');
    setNationalTeamSuggestions([]);
  }, []);

  // Handle modal drag movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!personModalDrag.current.active) return;
      const dx = e.clientX - personModalDrag.current.startX;
      const dy = e.clientY - personModalDrag.current.startY;
      setPersonModalPos({
        x: personModalDrag.current.startPosX + dx,
        y: personModalDrag.current.startPosY + dy,
      });
    };

    const handleMouseUp = () => {
      personModalDrag.current.active = false;
    };

    if (showPersonModal) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showPersonModal]);

  return {
    // State
    showPersonModal,
    setShowPersonModal,
    personModalPos,
    setPersonModalPos,
    phase,
    query,
    setQuery,
    personForm,
    setPersonForm,
    modalPersonId,
    setModalPersonId,
    personSuggestions,
    matchHubSuggestions,
    matchHubPrefill,

    // Primary team
    teamSearchQuery,
    setTeamSearchQuery,
    teamSuggestions,
    selectedTeam,

    // National team toggle (create phase only)
    showNationalTeamPicker,
    nationalTeamSearchQuery,
    setNationalTeamSearchQuery,
    nationalTeamSuggestions,
    selectedNationalTeam,
    toggleNationalTeamPicker,
    selectNationalTeam,
    clearNationalTeam,

    // Handlers
    openPersonModal,
    closePersonModal,
    enterCreatePhase,
    enterCreateFromMatchHub,
    enterSearchPhase,
    handleModalDragStart,
    selectTeam,
    clearTeam,
  };
}
